/**
 * Memberships & Certificates Page — F009
 *
 * Tabbed page: Memberships | Certificates
 * Location: Control Centre > Compliance > Memberships & Certificates
 *
 * Schema fields (from actual route — NOT spec):
 *  Memberships:
 *   - membershipType: industry | professional | aggregator | other
 *   - membershipStatus: active | expired | suspended | cancelled (NO pending_renewal)
 *   - membershipNumber (NOT memberNumber), contactName (NOT memberName)
 *   - No membershipLevel field
 *
 *  Certificates:
 *   - certificateType: diploma | cert_iv | degree | accreditation | lender_accreditation | other
 *   - holderType: organisation | credit_representative | staff
 *   - certificateStatus: current | expired | revoked | pending_renewal
 *   - issuingBody (NOT issuingAuthority)
 *   - CR filtering via holderId + holderType='credit_representative' (creditRepresentativeId param)
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  X,
  Pencil,
  Trash2,
  Building2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ComplianceLayout } from "./compliance-layout";
import {
  MembershipModal,
  type MembershipData,
} from "@/components/modals/membership-modal";
import {
  CertificateModal,
  type CertificateData,
} from "@/components/modals/certificate-modal";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Membership extends MembershipData {
  id: string;
  membershipType: string;
  organisationName: string;
  membershipStatus: string;
  createdAt: string;
  daysUntilExpiry: number | null;
}

interface Certificate extends CertificateData {
  id: string;
  certificateType: string;
  certificateName: string;
  holderType: string;
  issuingBody: string;
  issueDate: string;
  certificateStatus: string;
  createdAt: string;
  daysUntilExpiry: number | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MEMBERSHIP_TYPE_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  industry: {
    label: "Industry",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  professional: {
    label: "Professional",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  aggregator: {
    label: "Aggregator",
    className: "bg-orange-100 text-orange-700 border-orange-300",
  },
  other: {
    label: "Other",
    className: "bg-gray-100 text-gray-700 border-gray-300",
  },
};

const MEMBERSHIP_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  active: {
    label: "Active",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  expired: {
    label: "Expired",
    className: "bg-red-100 text-red-700 border-red-300",
  },
  suspended: {
    label: "Suspended",
    className: "bg-amber-100 text-amber-700 border-amber-300",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-gray-100 text-gray-700 border-gray-300",
  },
};

const CERTIFICATE_TYPE_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  diploma: {
    label: "Diploma",
    className: "bg-blue-100 text-blue-700 border-blue-300",
  },
  cert_iv: {
    label: "Certificate IV",
    className: "bg-purple-100 text-purple-700 border-purple-300",
  },
  degree: {
    label: "Degree",
    className: "bg-teal-100 text-teal-700 border-teal-300",
  },
  accreditation: {
    label: "Accreditation",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  lender_accreditation: {
    label: "Lender Accred.",
    className: "bg-orange-100 text-orange-700 border-orange-300",
  },
  other: {
    label: "Other",
    className: "bg-gray-100 text-gray-700 border-gray-300",
  },
};

const CERTIFICATE_STATUS_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  current: {
    label: "Current",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  pending_renewal: {
    label: "Pending Renewal",
    className: "bg-yellow-100 text-yellow-700 border-yellow-300",
  },
  expired: {
    label: "Expired",
    className: "bg-red-100 text-red-700 border-red-300",
  },
  revoked: {
    label: "Revoked",
    className: "bg-red-50 text-red-700 border-red-400",
  },
};

const MEMBERSHIP_TYPE_OPTIONS = [
  { value: "none", label: "All Types" },
  { value: "industry", label: "Industry" },
  { value: "professional", label: "Professional" },
  { value: "aggregator", label: "Aggregator" },
  { value: "other", label: "Other" },
];

const MEMBERSHIP_STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
];

const CERTIFICATE_TYPE_OPTIONS = [
  { value: "none", label: "All Types" },
  { value: "diploma", label: "Diploma" },
  { value: "cert_iv", label: "Certificate IV" },
  { value: "degree", label: "Degree" },
  { value: "accreditation", label: "Accreditation" },
  { value: "lender_accreditation", label: "Lender Accreditation" },
  { value: "other", label: "Other" },
];

const CERTIFICATE_HOLDER_TYPE_OPTIONS = [
  { value: "none", label: "All Holder Types" },
  { value: "organisation", label: "Organisation" },
  { value: "credit_representative", label: "Credit Representative" },
  { value: "staff", label: "Staff" },
];

const CERTIFICATE_STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "current", label: "Current" },
  { value: "pending_renewal", label: "Pending Renewal" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

/**
 * Returns a row className based on days until expiry.
 * - Red bg if expired (daysUntilExpiry <= 0) or <= 7 days
 * - Amber bg if <= 30 days
 */
function expiryRowClass(daysUntilExpiry: number | null): string {
  if (daysUntilExpiry === null) return "";
  if (daysUntilExpiry <= 7) return "bg-red-50 hover:bg-red-100";
  if (daysUntilExpiry <= 30) return "bg-amber-50 hover:bg-amber-100";
  return "";
}

// ---------------------------------------------------------------------------
// Badge components
// ---------------------------------------------------------------------------

function MembershipTypeBadge({ type }: { type: string }) {
  const config = MEMBERSHIP_TYPE_CONFIG[type] ?? {
    label: type,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function MembershipStatusBadge({ status }: { status: string }) {
  const config = MEMBERSHIP_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

function CertificateTypeBadge({ type }: { type: string }) {
  const config = CERTIFICATE_TYPE_CONFIG[type] ?? {
    label: type,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function CertificateStatusBadge({ status }: { status: string }) {
  const config = CERTIFICATE_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`cert-status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

function HolderTypeIcon({ holderType }: { holderType: string }) {
  if (holderType === "organisation") {
    return <Building2 className="h-4 w-4 inline mr-1 text-muted-foreground" />;
  }
  return <User className="h-4 w-4 inline mr-1 text-muted-foreground" />;
}

// ---------------------------------------------------------------------------
// Memberships Tab
// ---------------------------------------------------------------------------

function MembershipsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [membershipType, setMembershipType] = useUrlState("memType", "none");
  const [membershipStatus, setMembershipStatus] = useUrlState(
    "memStatus",
    "none"
  );
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [editingMembership, setEditingMembership] =
    useState<Membership | null>(null);
  const [deletingMembership, setDeletingMembership] =
    useState<Membership | null>(null);

  const queryParams = new URLSearchParams();
  if (membershipType !== "none") queryParams.set("membershipType", membershipType);
  if (membershipStatus !== "none") queryParams.set("status", membershipStatus);

  const { data, isLoading } = useQuery<Membership[]>({
    queryKey: ["/api/compliance/memberships", membershipType, membershipStatus],
    queryFn: async () => {
      const url = `/api/compliance/memberships${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const memberships = Array.isArray(data) ? data : [];

  const hasActiveFilters =
    membershipType !== "none" || membershipStatus !== "none";

  const clearFilters = () => {
    setMembershipType("none");
    setMembershipStatus("none");
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/memberships/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to delete membership"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/memberships"],
      });
      toast({ title: "Membership removed" });
      setDeletingMembership(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (m: Membership) => {
    setEditingMembership(m);
    setIsMembershipModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsMembershipModalOpen(open);
    if (!open) setEditingMembership(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters + Add */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={membershipType} onValueChange={setMembershipType}>
            <SelectTrigger
              className="w-[180px]"
              data-testid="select-filter-membership-type"
            >
              <SelectValue placeholder="Membership Type" />
            </SelectTrigger>
            <SelectContent>
              {MEMBERSHIP_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={membershipStatus} onValueChange={setMembershipStatus}>
            <SelectTrigger
              className="w-[160px]"
              data-testid="select-filter-membership-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {MEMBERSHIP_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        <Button
          data-testid="button-add-membership"
          onClick={() => {
            setEditingMembership(null);
            setIsMembershipModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Membership
        </Button>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Member Number</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Renewal Date</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : memberships.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No memberships tracked</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-add-membership-empty"
                      onClick={() => {
                        setEditingMembership(null);
                        setIsMembershipModalOpen(true);
                      }}
                    >
                      Add Membership
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              memberships.map((m) => (
                <TableRow
                  key={m.id}
                  data-testid={`row-membership-${m.id}`}
                  className={cn(
                    "hover:bg-muted/50",
                    expiryRowClass(m.daysUntilExpiry)
                  )}
                >
                  <TableCell className="font-medium">
                    {m.organisationName}
                  </TableCell>
                  <TableCell>
                    <MembershipTypeBadge type={m.membershipType} />
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {m.membershipNumber ?? "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {m.contactName ?? "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDate(m.renewalDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    <span
                      data-testid={`expiry-${m.id}`}
                      data-days={m.daysUntilExpiry ?? ""}
                    >
                      {formatDate(m.expiryDate)}
                      {m.daysUntilExpiry !== null && (
                        <span
                          className={cn(
                            "ml-1.5 text-xs",
                            m.daysUntilExpiry <= 7
                              ? "text-red-600 font-semibold"
                              : m.daysUntilExpiry <= 30
                                ? "text-amber-600"
                                : "text-muted-foreground"
                          )}
                        >
                          ({m.daysUntilExpiry}d)
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <MembershipStatusBadge status={m.membershipStatus} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Edit membership"
                        data-testid={`button-edit-membership-${m.id}`}
                        onClick={() => handleEdit(m)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        aria-label="Delete membership"
                        data-testid={`button-delete-membership-${m.id}`}
                        onClick={() => setDeletingMembership(m)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <MembershipModal
        open={isMembershipModalOpen}
        onOpenChange={handleModalClose}
        editingMembership={editingMembership}
      />

      <AlertDialog
        open={!!deletingMembership}
        onOpenChange={(open) => {
          if (!open) setDeletingMembership(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-delete-membership">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{deletingMembership?.organisationName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingMembership) {
                  deleteMutation.mutate(deletingMembership.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-membership"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certificates Tab
// ---------------------------------------------------------------------------

function CertificatesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [certType, setCertType] = useUrlState("certType", "none");
  const [certHolderType, setCertHolderType] = useUrlState(
    "certHolderType",
    "none"
  );
  const [certStatus, setCertStatus] = useUrlState("certStatus", "none");
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] =
    useState<Certificate | null>(null);
  const [deletingCertificate, setDeletingCertificate] =
    useState<Certificate | null>(null);

  const queryParams = new URLSearchParams();
  if (certType !== "none") queryParams.set("certificateType", certType);
  if (certHolderType !== "none") queryParams.set("holderType", certHolderType);
  if (certStatus !== "none") queryParams.set("status", certStatus);

  const { data, isLoading } = useQuery<Certificate[]>({
    queryKey: [
      "/api/compliance/certificates",
      certType,
      certHolderType,
      certStatus,
    ],
    queryFn: async () => {
      const url = `/api/compliance/certificates${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const certificates = Array.isArray(data) ? data : [];

  const hasActiveFilters =
    certType !== "none" || certHolderType !== "none" || certStatus !== "none";

  const clearFilters = () => {
    setCertType("none");
    setCertHolderType("none");
    setCertStatus("none");
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/certificates/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to delete certificate"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/certificates"],
      });
      toast({ title: "Certificate removed" });
      setDeletingCertificate(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (c: Certificate) => {
    setEditingCertificate(c);
    setIsCertModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsCertModalOpen(open);
    if (!open) setEditingCertificate(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters + Add */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={certType} onValueChange={setCertType}>
            <SelectTrigger
              className="w-[200px]"
              data-testid="select-filter-certificate-type"
            >
              <SelectValue placeholder="Certificate Type" />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICATE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={certHolderType} onValueChange={setCertHolderType}>
            <SelectTrigger
              className="w-[200px]"
              data-testid="select-filter-holder-type"
            >
              <SelectValue placeholder="Holder Type" />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICATE_HOLDER_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={certStatus} onValueChange={setCertStatus}>
            <SelectTrigger
              className="w-[160px]"
              data-testid="select-filter-certificate-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICATE_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        <Button
          data-testid="button-add-certificate"
          onClick={() => {
            setEditingCertificate(null);
            setIsCertModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Certificate
        </Button>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Holder</TableHead>
              <TableHead>Holder Type</TableHead>
              <TableHead>Certificate #</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : certificates.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No certificates tracked</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-add-certificate-empty"
                      onClick={() => {
                        setEditingCertificate(null);
                        setIsCertModalOpen(true);
                      }}
                    >
                      Add Certificate
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              certificates.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`row-certificate-${c.id}`}
                  className={cn(
                    "hover:bg-muted/50",
                    expiryRowClass(c.daysUntilExpiry)
                  )}
                >
                  <TableCell className="font-medium max-w-xs">
                    <span className="truncate block" title={c.certificateName}>
                      {c.certificateName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <CertificateTypeBadge type={c.certificateType} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.holderName ?? "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    <HolderTypeIcon holderType={c.holderType} />
                    <span className="capitalize">
                      {c.holderType === "credit_representative"
                        ? "CR"
                        : c.holderType}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {c.certificateNumber ?? "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDate(c.issueDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    <span
                      data-testid={`cert-expiry-${c.id}`}
                      data-days={c.daysUntilExpiry ?? ""}
                    >
                      {formatDate(c.expiryDate)}
                      {c.daysUntilExpiry !== null && (
                        <span
                          className={cn(
                            "ml-1.5 text-xs",
                            c.daysUntilExpiry <= 7
                              ? "text-red-600 font-semibold"
                              : c.daysUntilExpiry <= 30
                                ? "text-amber-600"
                                : "text-muted-foreground"
                          )}
                        >
                          ({c.daysUntilExpiry}d)
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <CertificateStatusBadge status={c.certificateStatus} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Edit certificate"
                        data-testid={`button-edit-certificate-${c.id}`}
                        onClick={() => handleEdit(c)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        aria-label="Delete certificate"
                        data-testid={`button-delete-certificate-${c.id}`}
                        onClick={() => setDeletingCertificate(c)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <CertificateModal
        open={isCertModalOpen}
        onOpenChange={handleModalClose}
        editingCertificate={editingCertificate}
      />

      <AlertDialog
        open={!!deletingCertificate}
        onOpenChange={(open) => {
          if (!open) setDeletingCertificate(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-delete-certificate">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{deletingCertificate?.certificateName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingCertificate) {
                  deleteMutation.mutate(deletingCertificate.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-certificate"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function MembershipsPageContent() {
  const [activeTab, setActiveTab] = useUrlState("tab", "memberships");

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Memberships &amp; Certificates</h1>
        <p className="text-sm text-muted-foreground">
          Industry memberships and professional certificates registry
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-0" aria-label="Memberships & Certificates tabs">
          <button
            type="button"
            data-testid="tab-memberships"
            onClick={() => setActiveTab("memberships")}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
              activeTab === "memberships"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            )}
          >
            Memberships
          </button>
          <button
            type="button"
            data-testid="tab-certificates"
            onClick={() => setActiveTab("certificates")}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
              activeTab === "certificates"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            )}
          >
            Certificates
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "memberships" ? <MembershipsTab /> : <CertificatesTab />}
    </div>
  );
}

export default function MembershipsPage() {
  return (
    <ComplianceLayout>
      <MembershipsPageContent />
    </ComplianceLayout>
  );
}
